/**
 * PATH: packages/ui/src/components/email/row/RowActions.tsx
 * FILE_ID: UI-EMAIL-ROW-ACTIONS-001
 * PURPOSE: The row's DISPOSITION toolbar — the per-row quick actions rendered
 *          IN-FLOW in the row's hover-revealed (desktop) / always-on (touch)
 *          bar BELOW the body. ONE consistent home for every disposition.
 * WHY:     This cluster has flip-flopped repeatedly (see EmailRow.tsx). v3
 *          settled it onto its own bar below the body; v5 moved the AI review
 *          ✓/✗ onto the work chip; v6 gave the icons verbs. v7 (2026-06-22,
 *          operator "the bottom rail only has three when it can be so much more
 *          cleanly structured") restructures it from a flat star · bin · junk
 *          run into THREE logically-grouped clusters separated by thin
 *          dividers, and fills in the obviously-missing high-frequency verbs
 *          (Read/Unread, Archive) so the bar reads as a complete, deliberate
 *          quick-actions toolbar:
 *            [ Keep ]      ★ Star
 *            [ Triage ]    ✉ Read/Unread · 🗄 Archive · 🗑 Bin
 *            [ Block ]     ⃠ Junk ▾
 *          Every verb reuses an existing host handler (toggleStar / toggleRead /
 *          archive / bulkAction-delete / auto-junk-rule) — NO new backend.
 * FLOW:    keep | triage (read/unread · archive|unarchive · bin|restore) | block.
 * RELATED:
 *   - row/RowWorkChip.tsx: owns the AI review ✓/✗ (always-visible on chip).
 *   - EmailRow.tsx: composes this into the bar below the body.
 *   - Test contract: "Bin email" / "Archive email" / "Unarchive email" /
 *     "Restore email" / "Star email" / "Mark read" / "Mark unread" aria-labels
 *     stay stable; the cluster is NOT absolute (see EmailRow.test.tsx).
 * NOTES FOR FUTURE AI:
 *   - When a row is already archived/binned, hosts wire onUnarchive / onRestore
 *     (RotateCcw reload arrow) instead of onArchive / onBin. Never show both
 *     Archive and Unarchive on the same row.
 */
import type { MouseEvent, ReactNode } from 'react'
import { Archive, Mail, MailOpen, RotateCcw, Star, Trash2 } from 'lucide-react'

import { cn } from '../../../lib/utils'

export interface RowActionsProps {
  isStarred: boolean
  onToggleStar: (event: MouseEvent<HTMLButtonElement>) => void
  /** Read/unread toggle. `isRead` is the CURRENT state — a click writes the
   *  opposite. Renders the ✉ verb only when a handler is wired (so hosts that
   *  don't expose read-state opt out cleanly). */
  isRead?: boolean
  onToggleRead?: (event: MouseEvent<HTMLButtonElement>) => void
  isTogglingRead?: boolean
  onBin?: (event: MouseEvent<HTMLButtonElement>) => void
  isBinning?: boolean
  /** Soft-delete inverse — shown instead of Bin when the row is already binned. */
  onRestore?: (event: MouseEvent<HTMLButtonElement>) => void
  isRestoring?: boolean
  onArchive?: (event: MouseEvent<HTMLButtonElement>) => void
  isArchiving?: boolean
  /** Archive inverse — shown instead of Archive when the row is already archived. */
  onUnarchive?: (event: MouseEvent<HTMLButtonElement>) => void
  isUnarchiving?: boolean
  junkControl?: ReactNode
}

/** Shared chrome for a LABELLED disposition button (icon + verb). The label
 *  widens the hit area past the ~44px touch target; `touch-manipulation` drops
 *  the ~300ms mobile tap delay so a tap actions immediately (operator
 *  2026-06-20: "problems clicking"). */
const ACTION_BTN =
  'inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-md text-[11px] font-medium transition-colors cursor-pointer shrink-0 touch-manipulation disabled:cursor-wait disabled:opacity-70'

/** Thin in-flow divider between the keep / triage / block clusters — the
 *  structure the operator asked for ("more cleanly structured"). Decorative. */
function ClusterDivider() {
  return <span aria-hidden className="mx-1 h-4 w-px shrink-0 self-center bg-border/70" />
}

/**
 * RowActions(...) → the row's DISPOSITION bar content, rendered inside
 * EmailRow's hover-revealed (desktop) / always-on (touch) bar BELOW the body.
 * Three grouped clusters: keep (star) · triage (read/unread, archive, bin) ·
 * block (junk). (The AI confirm/reject lives on the work chip, not here.)
 */
export function RowActions({
  isStarred,
  onToggleStar,
  isRead,
  onToggleRead,
  isTogglingRead = false,
  onBin,
  isBinning = false,
  onRestore,
  isRestoring = false,
  onArchive,
  isArchiving = false,
  onUnarchive,
  isUnarchiving = false,
  junkControl,
}: RowActionsProps) {
  // Whether the middle (triage) cluster has anything — drives its leading
  // divider so a divider never floats with nothing after it.
  const hasTriage = !!(onToggleRead || onArchive || onUnarchive || onBin || onRestore)

  return (
    <div
      data-testid="row-actions"
      // Borderless, LEFT-aligned toolbar: the disposition verbs sit directly
      // under the hairline EmailRow draws above them, grouped into clusters by
      // thin dividers so the bar reads as a deliberate footer toolbar (v7).
      // flex-wrap lets a cluster drop to a second line on a very narrow
      // split-view column instead of overflowing.
      className="flex flex-wrap items-center gap-0.5"
    >
      {/* ── Keep ─────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onToggleStar}
        title={isStarred ? 'Unstar' : 'Star'}
        aria-label={isStarred ? 'Unstar email' : 'Star email'}
        aria-pressed={isStarred}
        className={cn(
          ACTION_BTN,
          isStarred
            ? 'text-amber-500 hover:bg-amber-500/15'
            : 'text-muted-foreground/70 hover:bg-accent hover:text-foreground',
        )}
      >
        <Star className={cn('h-4 w-4 shrink-0', isStarred && 'fill-current')} />
        <span>{isStarred ? 'Starred' : 'Star'}</span>
      </button>

      {/* ── Triage ───────────────────────────────────────────── */}
      {hasTriage && <ClusterDivider />}

      {onToggleRead && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isTogglingRead) return
            onToggleRead(e)
          }}
          disabled={isTogglingRead}
          title={isRead ? 'Mark as unread' : 'Mark as read'}
          aria-label={isRead ? 'Mark unread' : 'Mark read'}
          className={cn(
            ACTION_BTN,
            'text-muted-foreground/70 hover:bg-accent hover:text-foreground',
          )}
        >
          {isRead ? (
            <Mail className="h-4 w-4 shrink-0" />
          ) : (
            <MailOpen className="h-4 w-4 shrink-0" />
          )}
          <span>{isRead ? 'Unread' : 'Read'}</span>
        </button>
      )}

      {/* Archive / Unarchive — durable sibling of Bin. When already archived,
          the host wires onUnarchive (reload arrow) instead of onArchive. */}
      {onUnarchive ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isUnarchiving) return
            onUnarchive(e)
          }}
          disabled={isUnarchiving}
          title="Unarchive (restore to inbox)"
          aria-label="Unarchive email"
          data-testid="row-action-unarchive"
          className={cn(
            ACTION_BTN,
            'text-muted-foreground/70 hover:bg-accent hover:text-foreground',
          )}
        >
          <RotateCcw className="h-4 w-4 shrink-0" />
          <span>Unarchive</span>
        </button>
      ) : onArchive ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isArchiving) return
            onArchive(e)
          }}
          disabled={isArchiving}
          title="Archive (move to Archive)"
          aria-label="Archive email"
          className={cn(
            ACTION_BTN,
            'text-muted-foreground/70 hover:bg-accent hover:text-foreground',
          )}
        >
          <Archive className="h-4 w-4 shrink-0" />
          <span>Archive</span>
        </button>
      ) : null}

      {/* Bin / Restore — soft-delete and its inverse. When already binned,
          the host wires onRestore (reload arrow) instead of onBin. */}
      {onRestore ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isRestoring) return
            onRestore(e)
          }}
          disabled={isRestoring}
          title="Restore (move back to inbox)"
          aria-label="Restore email"
          data-testid="row-action-restore"
          className={cn(
            ACTION_BTN,
            'text-muted-foreground/70 hover:bg-accent hover:text-foreground',
          )}
        >
          <RotateCcw className="h-4 w-4 shrink-0" />
          <span>Restore</span>
        </button>
      ) : onBin ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isBinning) return
            onBin(e)
          }}
          disabled={isBinning}
          title="Bin (move to Trash — recoverable)"
          aria-label="Bin email"
          className={cn(
            ACTION_BTN,
            'text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10',
          )}
        >
          <Trash2 className="h-4 w-4 shrink-0" />
          <span>Bin</span>
        </button>
      ) : null}

      {/* ── Block ────────────────────────────────────────────── */}
      {junkControl && (
        <>
          <ClusterDivider />
          <div className="shrink-0">{junkControl}</div>
        </>
      )}
    </div>
  )
}
